import { useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setIsSubmitted(true);
    setFormData({ name: "", email: "", message: "" });

    setTimeout(() => {
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
            {t("contact.title")}
          </h2>
          <p className="text-xl md:text-2xl text-neuro-cyan/80">
            {t("contact.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="flex flex-col gap-6">
            <a
              href="mailto:info@neurocell.com"
              className="group flex items-start gap-5 glass-neuro p-6 rounded-2xl border border-neuro-cyan/20 hover:border-neuro-cyan/40 transition-all duration-500 hover:scale-105 animate-slide-up neuro-glow-cyan cursor-pointer"
            >
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-neuro-cyan/10 flex items-center justify-center group-hover:bg-neuro-cyan/20 transition-all duration-300 group-hover:scale-110">
                <svg
                  className="w-7 h-7 text-neuro-cyan"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-neuro-cyan">
                  {t("contact.info.email")}
                </h4>
                <p className="text-gray-300 group-hover:text-neuro-cyan transition-colors">
                  info@neurocell.com
                </p>
              </div>
            </a>

            <a
              href="tel:+14155550123"
              className="group flex items-start gap-5 glass-neuro p-6 rounded-2xl border border-neuro-purple/20 hover:border-neuro-purple/40 transition-all duration-500 hover:scale-105 animate-slide-up animation-delay-100 neuro-glow-purple cursor-pointer"
            >
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-neuro-purple/10 flex items-center justify-center group-hover:bg-neuro-purple/20 transition-all duration-300 group-hover:scale-110">
                <svg
                  className="w-7 h-7 text-neuro-purple"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-neuro-purple">
                  {t("contact.info.phone")}
                </h4>
                <p className="text-gray-300 group-hover:text-neuro-purple transition-colors">
                  +1 (415) 555-0123
                </p>
              </div>
            </a>

            <a
              href="https://maps.google.com/?q=San+Francisco,CA"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-5 glass-neuro p-6 rounded-2xl border border-neuro-azure/20 hover:border-neuro-azure/40 transition-all duration-500 hover:scale-105 animate-slide-up animation-delay-200 neuro-glow-azure cursor-pointer"
            >
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-neuro-azure/10 flex items-center justify-center group-hover:bg-neuro-azure/20 transition-all duration-300 group-hover:scale-110">
                <svg
                  className="w-7 h-7 text-neuro-azure"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h4 className="font-bold mb-2 text-neuro-azure">
                  {t("contact.info.address")}
                </h4>
                <p className="text-gray-300 group-hover:text-neuro-azure transition-colors">
                  {t("contact.info.location")}
                </p>
              </div>
            </a>
          </div>

          <form
            className="lg:col-span-2 glass-neuro p-10 rounded-2xl border border-neuro-cyan/20 shadow-2xl shadow-neuro-cyan/10 hover:border-neuro-cyan/30 transition-all duration-500 animate-scale-in"
            onSubmit={handleSubmit}
          >
            {isSubmitted && (
              <div className="bg-gradient-to-r from-neuro-cyan/20 to-neuro-azure/20 border border-neuro-cyan/50 text-neuro-cyan p-4 rounded-xl mb-6 text-center animate-slide-up flex items-center justify-center gap-3 neuro-glow-cyan">
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                {t("contact.form.success")}
              </div>
            )}

            <div className="mb-6 group">
              <label
                htmlFor="name"
                className="block font-semibold mb-3 text-gray-200 group-focus-within:text-neuro-cyan transition-colors duration-300"
              >
                {t("contact.form.name")}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={t("contact.form.namePlaceholder")}
                className="w-full px-5 py-4 bg-dark-900/50 border-2 border-neuro-cyan/20 rounded-xl focus:border-neuro-cyan focus:ring-2 focus:ring-neuro-cyan/20 focus:outline-none transition-all duration-300 text-gray-100 placeholder-gray-500"
              />
            </div>

            <div className="mb-6 group">
              <label
                htmlFor="email"
                className="block font-semibold mb-3 text-gray-200 group-focus-within:text-neuro-cyan transition-colors duration-300"
              >
                {t("contact.form.email")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={t("contact.form.emailPlaceholder")}
                className="w-full px-5 py-4 bg-dark-900/50 border-2 border-neuro-cyan/20 rounded-xl focus:border-neuro-cyan focus:ring-2 focus:ring-neuro-cyan/20 focus:outline-none transition-all duration-300 text-gray-100 placeholder-gray-500"
              />
            </div>

            <div className="mb-8 group">
              <label
                htmlFor="message"
                className="block font-semibold mb-3 text-gray-200 group-focus-within:text-neuro-cyan transition-colors duration-300"
              >
                {t("contact.form.message")}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder={t("contact.form.messagePlaceholder")}
                className="w-full px-5 py-4 bg-dark-900/50 border-2 border-neuro-cyan/20 rounded-xl focus:border-neuro-cyan focus:ring-2 focus:ring-neuro-cyan/20 focus:outline-none transition-all duration-300 resize-vertical text-gray-100 placeholder-gray-500"
              />
            </div>

            <button
              type="submit"
              className="group relative w-full px-8 py-5 bg-gradient-to-r from-neuro-cyan to-neuro-azure text-dark-950 font-bold rounded-xl overflow-hidden transition-all duration-500 hover:scale-[1.02] neuro-glow-cyan flex items-center justify-center gap-3 cursor-pointer"
            >
              <span className="relative z-10">{t("contact.form.submit")}</span>
              <svg
                className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
              <div className="absolute inset-0 bg-gradient-to-r from-neuro-azure to-neuro-purple opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
